import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from 'baseui/button';
import { Input } from 'baseui/input';
import AuthContext from '../shared/AuthContext';
import AuthShell from '../auth/AuthShell';
import PageMeta from '../seo/PageMeta';
import { setDailyReviewGoal } from '../../utils/learningPreferences';
import '../onboarding/onboarding.css';
import '../../pages/learn/learn.css';

const GOALS = [
  { id: 'exam', label: 'Pass an exam or certification' },
  { id: 'interview', label: 'Interview preparation' },
  { id: 'mastery', label: 'Master a subject deeply' },
  { id: 'daily', label: 'Daily learning habit' },
];

const OnboardingPage = () => {
  const { apiClient } = useContext(AuthContext);
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [modules, setModules] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [learningGoal, setLearningGoal] = useState('');
  const [goalType, setGoalType] = useState('daily');
  const [dailyGoal, setDailyGoal] = useState('10');
  const [difficulty, setDifficulty] = useState('INTERMEDIATE');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get('/api/v1/modules', { params: search ? { search } : {} });
        if (!cancelled) setModules(Array.isArray(res.data) ? res.data : []);
      } catch {
        if (!cancelled) setError('Could not load learning modules.');
      }
    })();
    return () => { cancelled = true; };
  }, [apiClient, search]);

  const filteredModules = useMemo(() => modules, [modules]);
  const noModulesSelected = selectedIds.size === 0;

  const toggleModule = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const finish = async (skipped) => {
    setError('');
    setLoading(true);
    try {
      const goalLabel = GOALS.find((g) => g.id === goalType)?.label || '';
      const focus = learningGoal.trim() || goalLabel;
      await apiClient.put('/api/v1/learning/preferences', {
        learningFocus: focus,
        learningGoal: focus,
        experienceLevel: difficulty.toLowerCase(),
        preferredDifficulty: difficulty,
        dailyGoal: Number(dailyGoal) || 10,
        selectedModuleIds: skipped ? [] : [...selectedIds],
        onboardingCompleted: true,
        onboardingSkipped: skipped,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      localStorage.setItem('qnaread.onboardingDone', '1');
      setDailyReviewGoal(Number(dailyGoal) || 10);
      if (skipped || selectedIds.size === 0) {
        navigate('/hub#learn-library', { replace: true });
      } else {
        navigate('/hub#learn-library', { replace: true });
      }
    } catch {
      setError('Could not save your profile. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const steps = ['Welcome', 'Modules', 'Goal', 'Daily target', 'Finish'];

  return (
    <>
      <PageMeta title="Welcome to QnaRead" description="Tell QnaRead what you want to learn" />
      <AuthShell
        title={step === 0 ? 'Welcome to QnaRead' : 'What do you want to learn?'}
        subtitle="Personalize your space in a few taps — skip anything and change it later in Settings → Learning."
      >
        <div className="onboarding-flow">
          <div className="onboarding-steps" aria-hidden>
            {steps.map((_, i) => (
              <span key={steps[i]} className={`onboarding-step-dot${i === step ? ' active' : ''}`} />
            ))}
          </div>
          {error && <div className="auth-error" role="alert">{error}</div>}

          {step === 0 && (
            <>
              <p className="onboarding-copy">
                Set a daily goal and pick learning modules for recommendations. To read questions in Library, add
                notebooks from the catalog on <strong>Learn</strong> — or create a private custom notebook.
              </p>
              <Button
                onClick={() => setStep(1)}
                overrides={{ BaseButton: { style: { width: '100%' } } }}
              >
                Get started
              </Button>
              <Button
                kind="tertiary"
                onClick={() => finish(true)}
                overrides={{ BaseButton: { style: { width: '100%' } } }}
              >
                Skip for now
              </Button>
            </>
          )}

          {step === 1 && (
            <>
              <div className="onboarding-module-search">
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search modules…"
                />
              </div>
              <div className="onboarding-module-grid">
                {filteredModules.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    className={`onboarding-module-card${selectedIds.has(m.id) ? ' selected' : ''}`}
                    onClick={() => toggleModule(m.id)}
                  >
                    {m.iconKey} {m.title}
                  </button>
                ))}
              </div>
              {noModulesSelected && (
                <div className="onboarding-callout" role="note">
                  <strong>No module selected?</strong> You can still learn —{' '}
                  <Link to="/hub/notebooks/new">create a custom notebook</Link> on Learn and your questions will
                  appear in Library for you only. Add catalog notebooks on Learn after onboarding.
                </div>
              )}
              <div className="onboarding-actions">
                <Button onClick={() => setStep(2)} disabled={selectedIds.size === 0}>
                  Continue with modules
                </Button>
                <Button kind="tertiary" onClick={() => setStep(2)}>
                  Continue without modules
                </Button>
              </div>
            </>
          )}

          {step === 2 && (
            <div className="onboarding-field-group">
              <p className="onboarding-field-label">Learning goal</p>
              <ul className="onboarding-radio-list">
                {GOALS.map((g) => (
                  <li key={g.id}>
                    <label>
                      <input
                        type="radio"
                        name="goal"
                        checked={goalType === g.id}
                        onChange={() => setGoalType(g.id)}
                      />
                      {g.label}
                    </label>
                  </li>
                ))}
              </ul>
              <p className="onboarding-field-label">In your own words (optional)</p>
              <Input
                value={learningGoal}
                onChange={(e) => setLearningGoal(e.target.value)}
                placeholder="e.g. GATE CS 2026, Java backend role"
              />
              <Button onClick={() => setStep(3)} overrides={{ BaseButton: { style: { width: '100%' } } }}>
                Next
              </Button>
            </div>
          )}

          {step === 3 && (
            <div className="onboarding-field-group">
              <label className="onboarding-field-label" htmlFor="onboarding-daily-goal">
                Daily review target
              </label>
              <Input
                id="onboarding-daily-goal"
                type="number"
                value={dailyGoal}
                onChange={(e) => setDailyGoal(e.target.value)}
              />
              <label className="onboarding-field-label" htmlFor="onboarding-difficulty">
                Preferred difficulty
              </label>
              <select
                id="onboarding-difficulty"
                className="onboarding-select"
                value={difficulty}
                onChange={(e) => setDifficulty(e.target.value)}
              >
                <option value="BEGINNER">Beginner</option>
                <option value="INTERMEDIATE">Intermediate</option>
                <option value="ADVANCED">Advanced</option>
              </select>
              <Button onClick={() => setStep(4)} overrides={{ BaseButton: { style: { width: '100%' } } }}>
                Next
              </Button>
            </div>
          )}

          {step === 4 && (
            <>
              <p className="onboarding-summary">
                {noModulesSelected ? (
                  <>
                    You&apos;re ready to build your own path. After finishing, we&apos;ll open Learn so you can{' '}
                    <Link to="/hub/notebooks/new">create a custom notebook</Link> — or browse Library and add
                    catalog notebooks on <strong>Learn</strong>.
                  </>
                ) : (
                  <>
                    You&apos;re ready. Your module picks will appear in Library, and you can add more notebooks anytime.
                  </>
                )}
              </p>
              {!noModulesSelected && (
                <span className="onboarding-selected-count">{selectedIds.size} module(s) selected</span>
              )}
              <Button
                onClick={() => finish(false)}
                isLoading={loading}
                overrides={{ BaseButton: { style: { width: '100%', marginTop: 12 } } }}
              >
                {noModulesSelected ? 'Finish & open Learn' : 'Open my dashboard'}
              </Button>
            </>
          )}
        </div>
      </AuthShell>
    </>
  );
};

export default OnboardingPage;
